"use client";

import { Combobox as ComboboxPrimitive } from "@base-ui/react/combobox";
import { cn } from "cn";
import * as React from "react";
import { CheckIcon } from "../icons/CheckIcon";
import { ChevronDownIcon } from "../icons/ChevronDownIcon";
import { CloseIcon } from "../icons/CloseIcon";
import { Button } from "./Button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "./InputGroup";
import { usePortalContainer } from "./PortalContainer";

/**
 * A text input that filters a list of options to pick from. Single choice by
 * default; pass `multiple` to keep an array of choices.
 */
const Combobox = ComboboxPrimitive.Root;

/** Renders the current selection, or a function of it, outside the input. */
function ComboboxValue(props: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
}

/** A button that opens and closes the list. */
function ComboboxTrigger({
  className,
  children,
  ...props
}: ComboboxPrimitive.Trigger.Props) {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      className={cn(
        "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDownIcon className="text-muted-foreground" />
    </ComboboxPrimitive.Trigger>
  );
}

const inputGroupButtonClassName = "size-6 px-0 text-muted-foreground";

function ComboboxClear({ className, ...props }: ComboboxPrimitive.Clear.Props) {
  return (
    <ComboboxPrimitive.Clear
      data-slot="combobox-clear"
      aria-label="Clear"
      render={<Button variant="ghost" size="mini" />}
      className={cn(inputGroupButtonClassName, className)}
      {...props}
    >
      <CloseIcon />
    </ComboboxPrimitive.Clear>
  );
}

interface ComboboxInputProps extends ComboboxPrimitive.Input.Props {
  /** Shows a chevron button that toggles the list. */
  showTrigger?: boolean;
  /** Shows a × button while there is a selection; it replaces the chevron. */
  showClear?: boolean;
}

/** The single-choice field: an input styled like `InputGroup`. */
function ComboboxInput({
  className,
  children,
  disabled,
  showTrigger = true,
  showClear = false,
  ...props
}: ComboboxInputProps) {
  return (
    <ComboboxPrimitive.InputGroup
      render={<InputGroup />}
      className={cn("group/combobox-input", className)}
    >
      <ComboboxPrimitive.Input
        data-slot="combobox-input"
        disabled={disabled}
        render={<InputGroupInput className="pl-3" />}
        {...props}
      />
      {(showTrigger || showClear) && (
        <InputGroupAddon align="inline-end" className="gap-0.5 pr-1">
          {showTrigger && (
            <ComboboxPrimitive.Trigger
              data-slot="combobox-trigger"
              aria-label="Show options"
              disabled={disabled}
              render={<Button variant="ghost" size="mini" />}
              className={cn(
                inputGroupButtonClassName,
                "group-has-data-[slot=combobox-clear]/combobox-input:hidden",
              )}
            >
              <ChevronDownIcon />
            </ComboboxPrimitive.Trigger>
          )}
          {showClear && <ComboboxClear disabled={disabled} />}
        </InputGroupAddon>
      )}
      {children}
    </ComboboxPrimitive.InputGroup>
  );
}

interface ComboboxContentProps
  extends ComboboxPrimitive.Popup.Props,
    Pick<
      ComboboxPrimitive.Positioner.Props,
      | "side"
      | "align"
      | "sideOffset"
      | "alignOffset"
      | "anchor"
      | "positionMethod"
    >,
    Pick<ComboboxPrimitive.Portal.Props, "container"> {}

/**
 * The floating panel that holds the list. It matches the width of the input,
 * or of `anchor` when given (see `useComboboxAnchor`). It renders under
 * `<body>`, or inside the Dialog or Sheet it is opened from, so the mouse
 * still reaches it while that layer turns pointer events off outside itself;
 * `container` picks another element to render into. Inside a container it is
 * positioned against the viewport, so a container that scrolls never gains
 * overflow from the panel and never scrolls to it.
 */
function ComboboxContent({
  className,
  side = "bottom",
  sideOffset = 4,
  align = "start",
  alignOffset = 0,
  anchor,
  container,
  positionMethod,
  ...props
}: ComboboxContentProps) {
  const layerContainer = usePortalContainer();
  const resolvedContainer = container ?? layerContainer;
  return (
    <ComboboxPrimitive.Portal container={resolvedContainer}>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        positionMethod={
          positionMethod ?? (resolvedContainer ? "fixed" : undefined)
        }
        className="isolate z-50"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          className={cn(
            "group/combobox-content flex flex-col overflow-hidden outline-none",
            "w-(--anchor-width) max-w-(--available-width) max-h-(--available-height)",
            "origin-(--transform-origin)",
            "bg-card text-foreground",
            "rounded-md border border-border shadow-lg",
            "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95",
            "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            "duration-200",
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

/** The scrolling list of options; takes a render function per item. */
function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={cn(
        "max-h-72 min-h-0 scroll-py-1 overflow-y-auto overscroll-contain p-1",
        "data-empty:p-0",
        className,
      )}
      {...props}
    />
  );
}

/** One option. A check mark marks it while it is selected. */
function ComboboxItem({
  className,
  children,
  ...props
}: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        "relative flex w-full cursor-pointer items-center gap-2 rounded-sm",
        "py-2 pr-12 pl-2 body-md text-foreground outline-none select-none",
        "data-highlighted:bg-accent",
        "data-disabled:pointer-events-none data-disabled:opacity-50",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ItemIndicator
        render={
          <span className="absolute right-2 flex items-center justify-center text-foreground" />
        }
      >
        <CheckIcon className="size-4" />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  );
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
  return (
    <ComboboxPrimitive.Group
      data-slot="combobox-group"
      className={className}
      {...props}
    />
  );
}

/** The heading of a `ComboboxGroup`. */
function ComboboxLabel({
  className,
  ...props
}: ComboboxPrimitive.GroupLabel.Props) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={cn("px-2 py-1 caption-md text-muted-foreground", className)}
      {...props}
    />
  );
}

/** Renders the filtered items of a group through a render function. */
function ComboboxCollection(props: ComboboxPrimitive.Collection.Props) {
  return (
    <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />
  );
}

/** The message shown in place of the list when no option matches. */
function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        "hidden justify-center px-2 py-3 text-center body-sm text-muted-foreground",
        "group-data-empty/combobox-content:flex",
        className,
      )}
      {...props}
    />
  );
}

function ComboboxSeparator({
  className,
  ...props
}: ComboboxPrimitive.Separator.Props) {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      className={cn("-mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  );
}

/**
 * The multiple-choice field: an input-styled box holding a `ComboboxChip` per
 * selection followed by a `ComboboxChipsInput`.
 */
function ComboboxChips({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Chips>) {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      className={cn(
        "flex min-h-9 w-full min-w-0 cursor-text flex-wrap items-center gap-1 rounded-sm",
        "border border-border bg-card px-3 py-1",
        "has-data-[slot=combobox-chip]:px-1",
        "transition-[border-color,box-shadow]",
        "has-[input:focus-visible]:border-ring",
        "has-[input:focus-visible]:ring-[3px] has-[input:focus-visible]:ring-ring/50",
        "has-[input[aria-invalid=true]]:border-(--notification-destructive-fg)",
        "has-[input:disabled]:pointer-events-none has-[input:disabled]:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

interface ComboboxChipProps extends ComboboxPrimitive.Chip.Props {
  /** Shows a × button that removes this value from the selection. */
  showRemove?: boolean;
}

/** A pill for one selected value inside `ComboboxChips`. */
function ComboboxChip({
  className,
  children,
  showRemove = true,
  ...props
}: ComboboxChipProps) {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={cn(
        "inline-flex max-w-full shrink-0 items-center gap-0.5 rounded-full",
        "bg-secondary py-0.5 pl-2 label-sm text-foreground whitespace-nowrap",
        showRemove ? "pr-0.5" : "pr-2",
        "outline-none data-highlighted:ring-[3px] data-highlighted:ring-ring/50",
        "data-disabled:pointer-events-none data-disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          data-slot="combobox-chip-remove"
          aria-label="Remove"
          className={cn(
            "inline-flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full",
            "text-muted-foreground outline-none transition-colors",
            "hover:bg-(--cta-secondary-hover) hover:text-foreground",
            "focus-visible:ring-[3px] focus-visible:ring-ring/50",
            "disabled:pointer-events-none",
          )}
        >
          <CloseIcon className="size-3" />
        </ComboboxPrimitive.ChipRemove>
      )}
    </ComboboxPrimitive.Chip>
  );
}

/** The text input that follows the chips inside `ComboboxChips`. */
function ComboboxChipsInput({
  className,
  ...props
}: ComboboxPrimitive.Input.Props) {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-chip-input"
      className={cn(
        "min-w-16 flex-1 bg-transparent body-md text-foreground outline-none",
        "placeholder:text-muted-foreground disabled:cursor-not-allowed",
        className,
      )}
      {...props}
    />
  );
}

/**
 * A ref for `ComboboxChips`, passed as `anchor` to `ComboboxContent` so the
 * list lines up with the whole chips box rather than the text input.
 */
function useComboboxAnchor() {
  return React.useRef<HTMLDivElement | null>(null);
}

export {
  Combobox,
  ComboboxChip,
  type ComboboxChipProps,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  type ComboboxContentProps,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  type ComboboxInputProps,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
};
